/* eslint-disable arrow-body-style */
import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import {
  Modal,
  Fade,
  Backdrop,
  Grid,
  Card,
  CardHeader,
  Button,
  Divider,
  Box,
  CircularProgress,
  Stack
} from '@material-ui/core';
import Alert from '@mui/material/Alert';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import LifecycleEditor, {
  getLifecycleValues,
  buildLifecyclePayload
} from '../../lifecycle/LifecycleEditor';
import ConfirmDialog from '../../ConfirmDialog';
import { setOpenSnackbar } from '../../../store/slices/snackbarSlice';
import useVrackApi from '../../../hooks/useVrackApi';

const modalStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '100%',
  maxHeight: '90vh',
  p: 4,
  outline: 'none'
};

RackLifecycleModal.propTypes = {
  isOpen: PropTypes.bool,
  closeHandler: PropTypes.func,
  rackName: PropTypes.string
};

RackLifecycleModal.defaultProps = {
  isOpen: false,
  closeHandler: () => {},
  rackName: ''
};

export default function RackLifecycleModal({ isOpen, closeHandler, rackName }) {
  const dispatch = useDispatch();
  const openSnackbar = (payload) => dispatch(setOpenSnackbar(payload));

  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isError, setIsError] = useState(false);
  const [confirmClearOpen, setConfirmClearOpen] = useState(false);
  const [values, setValues] = useState(getLifecycleValues());

  // Disable Save when an enabled policy has an empty window-days field
  const hasEmptyWindowDays =
    (values.shutdownEnabled && values.shutdownWindowDays === '') ||
    (values.deletionEnabled && values.deletionWindowDays === '');

  // Initialize useVrackApi hooks for lifecycle endpoints
  const { fetchData: getRackLifecycle } = useVrackApi('get', '/racks/lifecycle');
  const { fetchData: updateRackLifecycle } = useVrackApi('post', '/admin/features/lifecycle');
  const { fetchData: clearRackLifecycle } = useVrackApi('delete', '/admin/features/lifecycle');

  const fetchRackLifecycleData = async () => {
    setIsLoading(true);
    try {
      const response = await getRackLifecycle({
        url: `/racks/lifecycle?rackName=${encodeURIComponent(rackName)}`
      });
      setValues(getLifecycleValues(response.lifecycle));
    } catch (err) {
      openSnackbar({
        message: err.response?.data?.message || 'Failed to load lifecycle defaults',
        severity: 'error'
      });
      setIsError(true);
    } finally {
      setIsLoading(false);
    }
  };

  // Load the rack's current lifecycle override when the modal opens.
  useEffect(() => {
    if (isOpen) {
      fetchRackLifecycleData();
    }
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [isOpen]);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await updateRackLifecycle({
        data: { rackName, lifecycle: buildLifecyclePayload(values) }
      });
      openSnackbar({ message: 'Updated rack lifecycle successfully!' });
      closeHandler();
    } catch (err) {
      openSnackbar({
        message: err.response?.data?.message || 'Failed to update rack lifecycle',
        severity: 'error'
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleClearConfirm = async () => {
    setConfirmClearOpen(false);
    try {
      await clearRackLifecycle({ data: { rackName } });
      openSnackbar({ message: 'Cleared rack lifecycle override successfully!' });
      closeHandler();
    } catch (err) {
      openSnackbar({
        message: err.response?.data?.message || 'Failed to clear rack lifecycle override',
        severity: 'error'
      });
    }
  };

  return (
    <>
      <ConfirmDialog
        title="Clear Lifecycle Override?"
        content="This will remove the lifecycle override for this rack, causing it to inherit the global defaults."
        onCancelHandler={() => setConfirmClearOpen(false)}
        onConfirmHandler={handleClearConfirm}
        isOpen={confirmClearOpen}
      />
      <Modal
        open={isOpen}
        onClose={closeHandler}
        aria-labelledby="rack-lifecycle"
        aria-describedby="modal to edit the lifecycle override for a virtual rack"
        BackdropComponent={Backdrop}
        data-testid="rack-lifecycle-modal"
      >
        <Fade in={isOpen}>
          <Grid container item direction="row" sx={modalStyle}>
            <Grid item xs={false} md={3} lg={4} onClick={closeHandler} />
            <Grid item xs={12} md={6} lg={4}>
              <Card>
                <CardHeader
                  title={`Lifecycle — ${rackName}`}
                  subheader="Configure the lifecycle settings for this rack."
                />
                {isLoading ? (
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      flex: 1
                    }}
                  >
                    <CircularProgress />
                  </Box>
                ) : (
                  <>
                    {isError ? (
                      <Box sx={{ p: 3 }}>
                        <Alert severity="error">Failed to load default lifecycle values.</Alert>
                      </Box>
                    ) : (
                      <>
                        <Box sx={{ p: 3 }}>
                          {/* Lifecycle Editor (Shutdown/Deletion Policies) */}
                          <LifecycleEditor values={values} onChange={setValues} isEditing />
                        </Box>
                        <Divider />
                        <Stack
                          direction="row"
                          spacing={1}
                          justifyContent="space-between"
                          p={2}
                          width="100%"
                        >
                          <Button
                            onClick={closeHandler}
                            size="small"
                            color="secondary"
                            startIcon={<CloseOutlinedIcon />}
                            variant="outlined"
                            sx={{ minWidth: { sm: 80 } }}
                            data-testid="lifecycle-close-button"
                          >
                            Close
                          </Button>
                          <Stack direction="row" spacing={1}>
                            <Button
                              onClick={() => setConfirmClearOpen(true)}
                              size="small"
                              color="error"
                              variant="outlined"
                              disabled={isSaving}
                              data-testid="lifecycle-clear-button"
                            >
                              Clear Override
                            </Button>
                            <Button
                              onClick={handleSave}
                              size="small"
                              color="primary"
                              variant="contained"
                              disabled={isSaving || hasEmptyWindowDays}
                              data-testid="lifecycle-save-button"
                            >
                              {isSaving ? 'Saving…' : 'Save'}
                            </Button>
                          </Stack>
                        </Stack>
                      </>
                    )}
                  </>
                )}
              </Card>
              <Grid item xs={false} md={3} lg={4} onClick={closeHandler} />
            </Grid>
          </Grid>
        </Fade>
      </Modal>
    </>
  );
}
